import { DPD_BUCKETS, bucketForDpd } from "@/lib/cibil/codes";
import { monthKey } from "./format";
import type { NormalizedAccount, NormalizedReport } from "./normalize";

const sum = (xs: (number | null)[]) => xs.reduce<number>((a, b) => a + (b ?? 0), 0);

export interface BureauMetrics {
  referenceDate: Date;
  activeCount: number;
  closedCount: number;
  totalSanctioned: number;
  totalOutstanding: number;
  creditUtilisation: number | null;
  utilisationSource: string;
  enquiries90: number;
  enquiries365: number;
  accountsWithDpd: number;
  totalDpdRecorded: number;
  currentOverdue: number;
  highestDpd: number | null;
}

export function computeMetrics(report: NormalizedReport): BureauMetrics {
  const { accounts, enquiries } = report;
  const active = accounts.filter((a) => a.isActive);
  const referenceDate =
    report.generatedAt ??
    accounts.reduce<Date | null>(
      (max, a) => (a.lastReported && (!max || a.lastReported > max) ? a.lastReported : max),
      null,
    ) ??
    new Date();

  const limitAccounts = accounts.filter((a) => a.creditLimit && a.creditLimit > 0);
  const limitTotal = sum(limitAccounts.map((a) => a.creditLimit));
  const balanceTotal = sum(limitAccounts.map((a) => a.outstanding));
  let creditUtilisation: number | null = null;
  let utilisationSource = "Not reported";
  if (limitTotal > 0) {
    creditUtilisation = (balanceTotal / limitTotal) * 100;
    utilisationSource = `Based on ${limitAccounts.length} of ${accounts.length} accounts reporting a limit`;
  } else if (report.bureauUtilisation !== null) {
    creditUtilisation = report.bureauUtilisation;
    utilisationSource = "Bureau reported utilisation";
  }

  const within = (days: number) =>
    enquiries.filter(
      (e) =>
        e.date &&
        (referenceDate.getTime() - e.date.getTime()) / 86_400_000 <= days &&
        e.date <= referenceDate,
    ).length;

  const dpdAll = accounts.flatMap((a) => a.payments.map((p) => p.dpd)).filter((d): d is number => d !== null);

  return {
    referenceDate,
    activeCount: active.length,
    closedCount: accounts.length - active.length,
    totalSanctioned: sum(active.map((a) => a.sanctionedLimit)),
    totalOutstanding: sum(active.map((a) => a.outstanding)),
    creditUtilisation,
    utilisationSource,
    enquiries90: within(90),
    enquiries365: within(365),
    accountsWithDpd: accounts.filter((a) => a.dpdCount > 0).length,
    totalDpdRecorded: dpdAll.length,
    currentOverdue: sum(accounts.map((a) => a.currentOverdue)),
    highestDpd: dpdAll.length ? Math.max(...dpdAll) : null,
  };
}

/* ---------------- chart series ---------------- */

export interface RepaymentPoint {
  month: string;
  onTime: number;
  delayed: number;
  onTimeCount: number;
  delayedCount: number;
}

const isReported = (status: string) =>
  status.trim() !== "" && status.trim().toUpperCase() !== "XXX";

/**
 * Per-month amount proxy for repayment charts.
 * Prefer reported EMI; when missing (common — CIBIL often sends -1),
 * fall back so Amount-mode bars still render.
 */
function monthlyAmountProxy(a: NormalizedAccount): number {
  if (a.emi != null && a.emi > 0) return a.emi;
  if (
    a.outstanding != null &&
    a.outstanding > 0 &&
    a.tenureMonths != null &&
    a.tenureMonths > 0
  ) {
    return a.outstanding / a.tenureMonths;
  }
  if (a.lastPaymentAmount != null && a.lastPaymentAmount > 0) {
    return a.lastPaymentAmount;
  }
  if (
    a.sanctionedLimit != null &&
    a.sanctionedLimit > 0 &&
    a.tenureMonths != null &&
    a.tenureMonths > 0
  ) {
    return a.sanctionedLimit / a.tenureMonths;
  }
  if (a.outstanding != null && a.outstanding > 0) return a.outstanding;
  if (a.sanctionedLimit != null && a.sanctionedLimit > 0) return a.sanctionedLimit;
  return 0;
}

/**
 * Monthly repayment split on-time vs delayed over a rolling window anchored on
 * the report date, so the axis follows the calendar and months with nothing
 * reported still appear. Amount prefers reported EMI; falls back to outstanding /
 * tenure / last payment when EMI is not reported.
 */
export function repaymentHistory(
  accounts: NormalizedAccount[],
  months = 24,
  anchor?: Date | null,
): RepaymentPoint[] {
  const end = anchor ?? new Date();
  const map = new Map<string, RepaymentPoint>();
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(end.getFullYear(), end.getMonth() - i, 1);
    map.set(monthKey(d), {
      month: monthKey(d),
      onTime: 0,
      delayed: 0,
      onTimeCount: 0,
      delayedCount: 0,
    });
  }
  for (const a of accounts) {
    const amount = monthlyAmountProxy(a);
    for (const p of a.payments) {
      const row = map.get(monthKey(p.date));
      if (!row) continue;
      if (p.dpd && p.dpd > 0) {
        row.delayed += amount;
        row.delayedCount += 1;
      } else if (isReported(p.status)) {
        row.onTime += amount;
        row.onTimeCount += 1;
      }
    }
  }
  return [...map.values()];
}

/* ---------------- underwriting aggregates ---------------- */

export interface DpdWindow {
  months: number;
  label: string;
  delayedMonths: number;
  worstDpd: number | null;
  accounts: number;
}

export function dpdRecency(
  accounts: NormalizedAccount[],
  reference: Date,
  windows = [6, 12, 24],
): DpdWindow[] {
  return windows.map((months) => {
    const cutoff = new Date(reference.getFullYear(), reference.getMonth() - months, 1);
    let delayedMonths = 0;
    let worstDpd: number | null = null;
    const ids = new Set<string>();
    for (const a of accounts) {
      for (const p of a.payments) {
        if (!p.dpd || p.dpd <= 0 || p.date < cutoff || p.date > reference) continue;
        delayedMonths += 1;
        ids.add(a.id);
        if (worstDpd === null || p.dpd > worstDpd) worstDpd = p.dpd;
      }
    }
    return {
      months,
      label: `Last ${months}m`,
      delayedMonths,
      worstDpd,
      accounts: ids.size,
    };
  });
}

export interface Obligations {
  totalEmi: number;
  emiReportingCount: number;
  emiMissingCount: number;
  securedOutstanding: number;
  unsecuredOutstanding: number;
  revolvingOutstanding: number;
  revolvingLimit: number;
  revolvingUtilisation: number | null;
  oldestOpened: Date | null;
  vintageYears: number | null;
  averageAgeYears: number | null;
  openedLast6: number;
  openedLast12: number;
}

const yearsBetween = (a: Date, b: Date) => (a.getTime() - b.getTime()) / (365.25 * 86_400_000);

export function obligations(report: NormalizedReport, reference: Date): Obligations {
  const accounts = report.accounts;
  const active = accounts.filter((a) => a.isActive);
  const withEmi = active.filter((a) => a.emi && a.emi > 0);
  const revolving = active.filter((a) => a.isRevolving);
  const revolvingLimit = sum(revolving.map((a) => a.creditLimit));
  const revolvingOutstanding = sum(revolving.map((a) => a.outstanding));

  const opened = accounts.map((a) => a.sanctionDate).filter((d): d is Date => d !== null);
  const oldestOpened = opened.length
    ? opened.reduce((min, d) => (d < min ? d : min), opened[0]!)
    : null;
  const ages = opened.map((d) => yearsBetween(reference, d)).filter((y) => y >= 0);

  const openedWithin = (months: number) => {
    const cutoff = new Date(reference.getFullYear(), reference.getMonth() - months, reference.getDate());
    return accounts.filter((a) => a.sanctionDate && a.sanctionDate >= cutoff).length;
  };

  return {
    totalEmi: sum(withEmi.map((a) => a.emi)),
    emiReportingCount: withEmi.length,
    emiMissingCount: active.length - withEmi.length,
    securedOutstanding: sum(active.filter((a) => a.isSecured).map((a) => a.outstanding)),
    unsecuredOutstanding: sum(active.filter((a) => !a.isSecured).map((a) => a.outstanding)),
    revolvingOutstanding,
    revolvingLimit,
    revolvingUtilisation: revolvingLimit > 0 ? (revolvingOutstanding / revolvingLimit) * 100 : null,
    oldestOpened,
    vintageYears: oldestOpened ? yearsBetween(reference, oldestOpened) : null,
    averageAgeYears: ages.length ? ages.reduce((a, b) => a + b, 0) / ages.length : null,
    openedLast6: openedWithin(6),
    openedLast12: openedWithin(12),
  };
}

export interface RiskFlag {
  label: string;
  value: string;
  tone: "danger" | "warning" | "neutral";
  hint?: string | undefined;
}

const ADVERSE = ["Special Mention Account", "Substandard", "Doubtful", "Loss"];

export function riskFlags(report: NormalizedReport, reference: Date): RiskFlag[] {
  const accounts = report.accounts;
  const has = (a: NormalizedAccount, needle: string) =>
    a.remarks.some((r) => r.toLowerCase().includes(needle.toLowerCase()));

  const flags: RiskFlag[] = [];
  const push = (label: string, n: number, tone: RiskFlag["tone"], hint?: string) => {
    if (n > 0) flags.push({ label, value: String(n), tone, hint });
  };

  push("Written off", accounts.filter((a) => has(a, "Written Off")).length, "danger");
  push("Settled", accounts.filter((a) => has(a, "Settled")).length, "danger");
  push("Suit filed", accounts.filter((a) => has(a, "Suit Filed")).length, "danger");
  push("Wilful default", accounts.filter((a) => has(a, "Wilful Default")).length, "danger");

  // Credit Facility Status (AccountCondition, abbreviation = creditFacilityStatus)
  const facility = new Map<string, number>();
  for (const a of accounts) {
    if (!a.creditFacilityStatus) continue;
    facility.set(a.creditFacilityStatus, (facility.get(a.creditFacilityStatus) ?? 0) + 1);
  }
  for (const [label, n] of [...facility.entries()].sort((x, y) => y[1] - x[1])) {
    flags.push({
      label: `Credit Facility Status: ${label}`,
      value: String(n),
      tone: "danger",
      hint: "AccountCondition reported by the lender",
    });
  }

  // Suit Filed / Wilful Default (suitFiledStatus)
  const suit = new Map<string, number>();
  for (const a of accounts) {
    if (!a.suitFiledStatus || a.suitFiledStatus === "No Suit Filed") continue;
    suit.set(a.suitFiledStatus, (suit.get(a.suitFiledStatus) ?? 0) + 1);
  }
  for (const [label, n] of [...suit.entries()].sort((x, y) => y[1] - x[1])) {
    flags.push({
      label: `Suit Filed / Wilful Default: ${label}`,
      value: String(n),
      tone: "danger",
      hint: "Suit filed status reported by the lender",
    });
  }
  push(
    "Adverse classification",
    accounts.filter((a) => a.worstClassification && ADVERSE.includes(a.worstClassification)).length,
    "danger",
    "SMA / SUB / DBT / LSS reported in payment history",
  );
  push(
    "Currently overdue",
    accounts.filter((a) => a.isActive && (a.currentOverdue ?? 0) > 0).length,
    "danger",
  );
  push("Disputed", accounts.filter((a) => a.disputes.length > 0).length, "warning");
  push(
    "Joint / Guarantor",
    accounts.filter(
      (a) => a.ownership && /joint|guarantor/i.test(a.ownership),
    ).length,
    "warning",
    "Contingent liability outside sole exposure",
  );
  push(
    "Stale reporting (>90d)",
    accounts.filter(
      (a) =>
        a.isActive &&
        a.lastReported &&
        (reference.getTime() - a.lastReported.getTime()) / 86_400_000 > 90,
    ).length,
    "warning",
  );

  const byLender = new Map<string, number>();
  for (const a of accounts.filter((x) => x.isActive)) {
    const key = a.investorName ?? "Unknown";
    byLender.set(key, (byLender.get(key) ?? 0) + (a.outstanding ?? 0));
  }
  const totalOs = [...byLender.values()].reduce((x, y) => x + y, 0);
  const top = [...byLender.entries()].sort((a, b) => b[1] - a[1])[0];
  if (top && totalOs > 0) {
    flags.push({
      label: "Top lender concentration",
      value: `${((top[1] / totalOs) * 100).toFixed(0)}%`,
      tone: top[1] / totalOs > 0.5 ? "warning" : "neutral",
      hint: top[0],
    });
  }
  return flags;
}

export interface EnquiryVelocity {
  label: string;
  days: number;
  count: number;
}

export function enquiryVelocity(report: NormalizedReport, reference: Date): EnquiryVelocity[] {
  const windows: [string, number][] = [
    ["7d", 7],
    ["30d", 30],
    ["90d", 90],
    ["180d", 180],
    ["12m", 365],
  ];
  return windows.map(([label, days]) => ({
    label,
    days,
    count: report.enquiries.filter(
      (e) =>
        e.date &&
        e.date <= reference &&
        (reference.getTime() - e.date.getTime()) / 86_400_000 <= days,
    ).length,
  }));
}

export interface OverviewPoint {
  label: string;
  value: number;
}

export function repaymentsOverview(accounts: NormalizedAccount[]): OverviewPoint[] {
  let onTime = 0;
  let delayed = 0;
  for (const a of accounts) {
    for (const p of a.payments) {
      const amount = monthlyAmountProxy(a);
      if (p.dpd && p.dpd > 0) delayed += amount;
      else if (p.status.trim() !== "" && p.status.toUpperCase() !== "XXX") onTime += amount;
    }
  }
  return [
    { label: "Sanctioned / High Credit", value: sum(accounts.map((a) => a.sanctionedLimit)) },
    { label: "Outstanding", value: sum(accounts.map((a) => a.outstanding)) },
    { label: "On-time Repayment", value: onTime },
    { label: "Delayed Repayment", value: delayed },
    { label: "Overdue", value: sum(accounts.map((a) => a.currentOverdue)) },
  ];
}

export interface TypeCount {
  type: string;
  total: number;
  withDelays: number;
}

export function dpdAccountsByType(accounts: NormalizedAccount[]): TypeCount[] {
  const map = new Map<string, TypeCount>();
  for (const a of accounts) {
    const type = a.loanType ?? "Unknown";
    const row = map.get(type) ?? { type, total: 0, withDelays: 0 };
    row.total += 1;
    if (a.dpdCount > 0) row.withDelays += 1;
    map.set(type, row);
  }
  return [...map.values()].sort((a, b) => b.total - a.total);
}

export interface DpdMatrix {
  bucket: string;
  label: string;
  total: number;
  years: { year: number; months: number[]; total: number }[];
}

/**
 * Monthly total of delay DAYS (sum of DPD values, not instance counts), with a
 * continuous year axis from the first year a DPD was reported through the
 * reference year.
 */
export function dpdDaysHeatmap(
  accounts: NormalizedAccount[],
  reference: Date,
): { year: number; months: number[]; total: number }[] {
  const years = new Map<number, number[]>();
  let first: number | null = null;
  for (const a of accounts) {
    for (const p of a.payments) {
      if (!p.dpd || p.dpd <= 0) continue;
      const y = p.date.getFullYear();
      if (first === null || y < first) first = y;
      const arr = years.get(y) ?? Array.from({ length: 12 }, () => 0);
      arr[p.date.getMonth()] = (arr[p.date.getMonth()] ?? 0) + p.dpd;
      years.set(y, arr);
    }
  }
  if (first === null) return [];
  const last = Math.max(reference.getFullYear(), ...years.keys());
  const rows: { year: number; months: number[]; total: number }[] = [];
  for (let y = first; y <= last; y++) {
    const months = years.get(y) ?? Array.from({ length: 12 }, () => 0);
    rows.push({ year: y, months, total: months.reduce((x, z) => x + z, 0) });
  }
  return rows;
}

export function dpdBifurcation(accounts: NormalizedAccount[]): DpdMatrix[] {
  const buckets: { key: string; label: string }[] = [
    ...DPD_BUCKETS.map((b) => ({ key: b.key as string, label: b.label })),
    { key: "all", label: "All DPDs" },
  ];

  return buckets.map(({ key, label }) => {
    const years = new Map<number, number[]>();
    let total = 0;
    for (const a of accounts) {
      for (const p of a.payments) {
        if (!p.dpd || p.dpd <= 0) continue;
        const b = bucketForDpd(p.dpd);
        if (key !== "all" && b !== key) continue;
        const y = p.date.getFullYear();
        const arr = years.get(y) ?? Array.from({ length: 12 }, () => 0);
        arr[p.date.getMonth()] = (arr[p.date.getMonth()] ?? 0) + 1;
        years.set(y, arr);
        total += 1;
      }
    }
    return {
      bucket: key,
      label,
      total,
      years: [...years.entries()]
        .sort((a, b) => b[0] - a[0])
        .map(([year, months]) => ({
          year,
          months,
          total: months.reduce((x, y) => x + y, 0),
        })),
    };
  });
}

export function accountsOpenedTrend(accounts: NormalizedAccount[], months = 36) {
  const map = new Map<string, number>();
  for (const a of accounts) {
    if (!a.sanctionDate) continue;
    const key = monthKey(a.sanctionDate);
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-months)
    .map(([month, count]) => ({ month, count }));
}

export function accountsByType(report: NormalizedReport) {
  const map = new Map<string, { type: string; enquiries: number; accounts: number }>();
  const get = (type: string) => {
    const row = map.get(type) ?? { type, enquiries: 0, accounts: 0 };
    map.set(type, row);
    return row;
  };
  for (const a of report.accounts) get(a.loanType ?? "Unknown").accounts += 1;
  for (const e of report.enquiries) get(e.purpose ?? "Unknown").enquiries += 1;
  return [...map.values()].sort((a, b) => b.accounts + b.enquiries - (a.accounts + a.enquiries));
}

const SIZE_BANDS = [
  { label: "< ₹1L", max: 1e5 },
  { label: "₹1L - ₹5L", max: 5e5 },
  { label: "₹5L - ₹25L", max: 25e5 },
  { label: "₹25L - ₹1Cr", max: 1e7 },
  { label: "> ₹1Cr", max: Number.POSITIVE_INFINITY },
];

export function accountsBySize(report: NormalizedReport) {
  const rows = SIZE_BANDS.map((b) => ({ band: b.label, enquiries: 0, accounts: 0 }));
  const bandIndex = (amount: number | null) => {
    if (amount === null) return -1;
    return SIZE_BANDS.findIndex((b) => amount < b.max);
  };
  for (const a of report.accounts) {
    const i = bandIndex(a.sanctionedLimit);
    if (i >= 0) rows[i]!.accounts += 1;
  }
  for (const e of report.enquiries) {
    const i = bandIndex(e.amount);
    if (i >= 0) rows[i]!.enquiries += 1;
  }
  return rows;
}
